import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const AreasWeServe = () => {
  // Areas data array
  const areasData = [
    {
      name: "Palm Jebel Ali",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1747819920_07e7f6db2bf3be55fd82.jpg",
      location: "Dubai, UAE",
    },
    {
      name: "Downtown Dubai",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1746090156_ff7e05ffd61e29ea72db.jpg",
      location: "Dubai, UAE",
    },
    {
      name: "Dubai Marina",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1746091840_587d4125a312abe3b272.jpg",
      location: "Dubai, UAE",
    },
    {
      name: "Dubai Hills Estate",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1746085307_24bd9316779ac8ef63fe.png",
      location: "Dubai, UAE",
    },
    {
      name: "Business Bay",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1746089677_1ea40dd359d3eeeab275.jpg",
      location: "Dubai, UAE",
    },
    {
      name: "Jumeirah Village Circle (JVC)",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1746088400_16e319fc859459775114.jpg",
      location: "Dubai, UAE",
    },
    {
      name: "Emaar South & Dubai Creek Harbour",
      image:
        "https://descorealestate.com/admin/uploads/area/202505/1746089218_9bd67f13575d4623b6a9.jpg",
      location: "Dubai, UAE",
    },
  ];

  return (
    <PageSection sx={{ bgcolor: "#fff" }}>
      <Container>
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <SectionTitle variant="h4" component="h2">
            Areas We Serve
          </SectionTitle>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mt: 2 }}
          >
            We specialize in some of Dubai's most iconic and high-growth
            communities
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {areasData.map((area, index) => {
            // Determine grid size based on index for the layout pattern
            const getGridSize = (index) => {
              if (index < 2) {
                // First 2 cards → 2 columns (md: 6)
                return { xs: 12, md: 6 };
              } else if (index < 5) {
                // Next 3 cards → 3 columns (md: 4)
                return { xs: 12, md: 4 };
              } else {
                // Last 2 cards → 2 columns (md: 6)
                return { xs: 12, md: 6 };
              }
            };
            return (
              <Grid item {...getGridSize(index)} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                >
                  <Card
                    sx={{
                      borderRadius: "15px",
                      overflow: "hidden",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      height: "100%",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 16px 40px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={area.image}
                      alt={area.name}
                      sx={{
                        objectFit: "cover",
                      }}
                    />
                    <CardContent sx={{ px: 3 }}>
                      <Typography
                        variant="h6"
                        component="h3"
                        fontWeight={600}
                        gutterBottom
                        sx={{ color: "#1a1a1a" }}
                      >
                        {area.name}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: "50%",
                            backgroundColor: "#C10E14",
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "14px" }}
                        >
                          {area.location}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: "16px", maxWidth: "600px", mx: "auto" }}
          >
            From waterfront views to investment-ready zones, we guide you to the
            right location based on your goals—whether lifestyle-driven or
            ROI-focused.
          </Typography>
        </Box>
      </Container>
    </PageSection>
  );
};

export default AreasWeServe;
